/**
 * PlayerStatsTableContainer - Container for player stats module
 *
 * Manages tab state and data for competition statistics,
 * transfer history, and injury history tabs. Fetches pre-computed
 * Transfermarkt basic stats via the listBasicCompetitionStats endpoint
 * (materialized view grouped by player/league/season).
 * Season options are filtered to only include seasons with actual basic
 * stats data, preventing empty "No Statistics Available" states (SE-3997).
 * @remarks This is a Container component - handles all business logic
 */

import React, { useCallback, useMemo, useState } from "react";

import PlayerStatsTableView from "./PlayerStatsTableView";

import type {
  CompetitionStatRow,
  InjuryRow,
  StatsTab,
  TabConfig,
  TimeframeOption,
  TransferRow,
} from "@/features/player-detail/types";

import {
  PlayerDetailPlayerMetaFragment,
  PlayerDetailTransferFragment,
  PlayerDetailInjuryFragment,
  PlayerDetailBasicCompetitionStatsFragment,
  usePlayerDetailGetTransfersByPlayerIdQuery,
  usePlayerDetailGetInjuriesByPlayerIdQuery,
  usePlayerDetailListBasicCompetitionStatsQuery,
  useRosterManagementListLeaguesQuery,
} from "@/generated/graphql";
import { POSTHOG_FLAGS } from "@/config/featureFlags";
import { useAppLocale } from "@/i18n";
import useMobileScreen from "@/hooks/useMobileScreen";
import usePostHogFeatureFlag from "@/hooks/usePostHogFeatureFlag";
import {
  buildSeasonOptions,
  buildSeasonOptionsFromBasicStats,
  buildSeasonIdToYearMap,
} from "@/features/player-detail/utils/performanceAnalyticsUtils";
import { useTheme } from "@/providers/ThemeProvider";
import {
  MEDIUM_DATE_OPTIONS,
  formatDate as formatIntlDate,
} from "@/utils/intlFormat";
import { TEST_ID_PLAYER_DETAIL } from "e2e/testId";

/**
 * Props for PlayerStatsTableContainer
 */
interface PlayerStatsTableContainerProps {
  /** Player data from parent query */
  player?: PlayerDetailPlayerMetaFragment | null;
  /** Whether dark mode is active */
  isDark?: boolean;
}

/** All available tabs for the player stats module */
const ALL_TABS: readonly TabConfig[] = [
  { id: "stats", label: "Basic Stats" },
  {
    id: "transfers",
    label: "Transfers",
    testID: TEST_ID_PLAYER_DETAIL.TRANSFERS_HEADING,
  },
  { id: "injuries", label: "Injuries" },
];

/** Default number of rows to show in card view before "View all" */
const DEFAULT_VISIBLE_ROWS = 5;

/** Maximum number of rows to show in "View all" modal */
const MAX_MODAL_ROWS = 20;

/**
 * Format date string to readable format, in the active UI locale.
 *
 * SE-7966: the locale was hardcoded `"en-US"`; it is now a parameter so
 * the transfer and injury tables follow the account's language.
 * @param dateString - ISO date string
 * @param locale - The active UI locale
 */
function formatDate(
  dateString: string | null | undefined,
  locale: string
): string {
  if (!dateString) return "\u2014";
  return formatIntlDate(dateString, locale, MEDIUM_DATE_OPTIONS) || "\u2014";
}

/**
 * Map transfer data to display rows
 * @param transfers - Transfer fragments from GraphQL
 * @param locale - The active UI locale, for the date column
 */
function mapTransfersToRows(
  transfers: readonly PlayerDetailTransferFragment[],
  locale: string
): readonly TransferRow[] {
  return transfers.map(transfer => ({
    id: transfer.id,
    from: transfer.sourceTeam?.name ?? "\u2014",
    to: transfer.destinationTeam?.name ?? "\u2014",
    fee: transfer.fee ?? "\u2014",
    date: formatDate(transfer.date, locale),
  }));
}

/**
 * Map injury data to display rows
 * @param injuries - Injury fragments from GraphQL
 * @param locale - The active UI locale, for the date column
 */
function mapInjuriesToRows(
  injuries: readonly PlayerDetailInjuryFragment[],
  locale: string
): readonly InjuryRow[] {
  return injuries.map(injury => ({
    id: injury.id,
    date: formatDate(injury.injuryDate, locale),
    injuryType: injury.injury ?? "Unknown",
    daysMissed: injury.daysMissed ?? 0,
  }));
}

/**
 * Compute a total row by summing already-computed competition rows.
 * Ensures total always matches the sum of visible competition rows.
 * @param competitionRows - Rows to sum
 */
export function computeTotalFromCompetitionRows(
  competitionRows: readonly CompetitionStatRow[]
): CompetitionStatRow | null {
  if (competitionRows.length === 0) return null;
  const totals = competitionRows.reduce(
    (acc, row) => ({
      matchesPlayed: acc.matchesPlayed + row.matchesPlayed,
      goals: acc.goals + row.goals,
      assists: acc.assists + row.assists,
      minutes: acc.minutes + row.minutes,
    }),
    { matchesPlayed: 0, goals: 0, assists: 0, minutes: 0 }
  );
  return {
    id: "total",
    name: "Total",
    ...totals,
    isTotal: true,
  };
}

/**
 * Map pre-computed basic competition stats to display rows.
 * Filters stats by the selected season's UUID, then groups by leagueId
 * with one row per league.
 * @param stats - BasicCompetitionStats array from backend materialized view
 * @param selectedSeason - ggUnifiedSeasonKey like "2024"
 * @param seasonOptions - Season options with seasonId UUIDs for lookup
 * @param leagueNameMap - League UUID to display name map
 */
export function mapBasicStatsToCompetitionRows(
  stats: readonly PlayerDetailBasicCompetitionStatsFragment[],
  selectedSeason: string,
  seasonOptions: readonly TimeframeOption[],
  leagueNameMap: Record<string, string>
): readonly CompetitionStatRow[] {
  const selectedOption = seasonOptions.find(opt => opt.id === selectedSeason);
  const targetSeasonId = selectedOption?.seasonId;
  const filteredStats = targetSeasonId
    ? stats.filter(s => s.seasonId === targetSeasonId)
    : [];

  // Group by leagueId and sum stats across rows
  const grouped = filteredStats.reduce(
    (acc, stat) => {
      const existing = acc[stat.leagueId];
      return {
        ...acc,
        [stat.leagueId]: {
          matchesPlayed:
            (existing?.matchesPlayed ?? 0) +
            Math.round(stat.basicAppearances ?? 0),
          goals: (existing?.goals ?? 0) + Math.round(stat.basicGoals ?? 0),
          assists:
            (existing?.assists ?? 0) + Math.round(stat.basicAssists ?? 0),
          minutes:
            (existing?.minutes ?? 0) + Math.round(stat.basicMinutes ?? 0),
        },
      };
    },
    {} as Record<
      string,
      {
        matchesPlayed: number;
        goals: number;
        assists: number;
        minutes: number;
      }
    >
  );

  const rows = Object.entries(grouped).map(
    ([leagueId, totals]): CompetitionStatRow => ({
      id: leagueId,
      name: leagueNameMap[leagueId] ?? leagueId,
      matchesPlayed: totals.matchesPlayed,
      goals: totals.goals,
      assists: totals.assists,
      minutes: totals.minutes,
      isTotal: false,
      isCompetitionRow: true,
    })
  );

  return [...rows].sort((a, b) => b.matchesPlayed - a.matchesPlayed);
}

/**
 * PlayerStatsTableContainer handles tab state and data transformation
 * @param props - Container props
 * @param props.player - Player data from parent query
 * @param props.isDark - Whether dark mode is active
 */
const PlayerStatsTableContainer = ({
  player,
  isDark: propIsDark,
}: PlayerStatsTableContainerProps) => {
  // 1. VARIABLES & DERIVED STATE
  const [activeTab, setActiveTab] = useState<StatsTab>("stats");
  const [isModalOpen, setIsModalOpen] = useState(false);
  // State uses ggUnifiedSeasonKey ("2024"); empty string means "use first available"
  const [selectedSeason, setSelectedSeason] = useState<string>("");

  const { resolvedTheme } = useTheme();
  const themeIsDark = resolvedTheme === "dark";
  const isDark = propIsDark ?? themeIsDark;
  const isSmallScreen = useMobileScreen();
  const locale = useAppLocale();
  const isBasicStatsEnabled =
    usePostHogFeatureFlag(POSTHOG_FLAGS.BASIC_STATS_TAB) === true;

  const visibleTabs = useMemo(
    () =>
      isBasicStatsEnabled ? ALL_TABS : ALL_TABS.filter(t => t.id !== "stats"),
    [isBasicStatsEnabled]
  );

  const playerId = player?.id;

  // Fetch transfers using dedicated query
  const { data: transfersData, loading: transfersLoading } =
    usePlayerDetailGetTransfersByPlayerIdQuery({
      variables: { playerId: playerId ?? "" },
      skip: !playerId,
    });

  // Fetch injuries using dedicated query
  const { data: injuriesData, loading: injuriesLoading } =
    usePlayerDetailGetInjuriesByPlayerIdQuery({
      variables: { playerId: playerId ?? "" },
      skip: !playerId,
    });

  // Fetch pre-computed basic stats from Transfermarkt materialized view
  const { data: basicStatsData, loading: basicStatsLoading } =
    usePlayerDetailListBasicCompetitionStatsQuery({
      variables: { playerId: playerId ?? "" },
      skip: !playerId,
      fetchPolicy: "cache-and-network",
    });

  // Fetch leagues to resolve leagueId (UUID) to league name
  const { data: leaguesData, loading: leaguesLoading } =
    useRosterManagementListLeaguesQuery({
      variables: { first: 1000 },
    });

  // Build league ID -> name lookup map
  const leagueNameMap = useMemo((): Record<string, string> => {
    const edges = leaguesData?.listLeagues?.edges ?? [];
    return edges.reduce(
      (acc, edge) => ({ ...acc, [edge.node.id]: edge.node.name }),
      {} as Record<string, string>
    );
  }, [leaguesData?.listLeagues?.edges]);

  // Map transfers from query data (fallback to player fragment)
  const allTransfers = useMemo(
    () =>
      mapTransfersToRows(
        transfersData?.transfersByPlayerId ?? player?.transfers ?? [],
        locale
      ),
    [transfersData?.transfersByPlayerId, player?.transfers, locale]
  );

  // Map injuries from query data (fallback to player fragment)
  const allInjuries = useMemo(
    () =>
      mapInjuriesToRows(
        injuriesData?.injuriesByPlayerId ?? player?.injuries ?? [],
        locale
      ),
    [injuriesData?.injuriesByPlayerId, player?.injuries, locale]
  );

  // Build season options; fall back to basic stats when seasonCategoricalScores is empty (e.g. goalkeepers).
  // Filter to only include seasons that have at least one basic stats record (SE-3997).
  const seasonOptions = useMemo(() => {
    const rawStats = basicStatsData?.listBasicCompetitionStats ?? [];

    const options = buildSeasonOptions(player);
    const baseOptions =
      options.length > 0
        ? options
        : buildSeasonOptionsFromBasicStats(
            rawStats,
            buildSeasonIdToYearMap(player, rawStats)
          );

    // Only filter when basic stats data is available — prevents stripping all options before data loads
    if (rawStats.length === 0) return baseOptions;

    const seasonIdsWithData = new Set(rawStats.map(s => s.seasonId));
    return baseOptions.filter(
      opt => !opt.seasonId || seasonIdsWithData.has(opt.seasonId)
    );
  }, [player, basicStatsData?.listBasicCompetitionStats]);

  // Derive effective season: user selection if valid, otherwise first available (most recent)
  const effectiveSelectedSeason = useMemo(() => {
    const match = seasonOptions.find(opt => opt.id === selectedSeason);
    return match ? selectedSeason : (seasonOptions[0]?.id ?? "");
  }, [selectedSeason, seasonOptions]);

  // Get selected season label for display
  const selectedSeasonLabel = useMemo(
    () =>
      seasonOptions.find(opt => opt.id === effectiveSelectedSeason)?.label ??
      "",
    [seasonOptions, effectiveSelectedSeason]
  );

  // Total row is computed from competition rows so totals always match visible rows
  const allStats = useMemo((): readonly CompetitionStatRow[] => {
    const rawStats = basicStatsData?.listBasicCompetitionStats ?? [];
    const competitionRows = mapBasicStatsToCompetitionRows(
      rawStats,
      effectiveSelectedSeason,
      seasonOptions,
      leagueNameMap
    );
    const totalRow = computeTotalFromCompetitionRows(competitionRows);
    return totalRow ? [totalRow, ...competitionRows] : competitionRows;
  }, [
    basicStatsData?.listBasicCompetitionStats,
    effectiveSelectedSeason,
    seasonOptions,
    leagueNameMap,
  ]);

  // Visible rows (limited for card view - Total + 4 competitions = 5 rows)
  const visibleStats = useMemo(
    () => allStats.slice(0, DEFAULT_VISIBLE_ROWS),
    [allStats]
  );

  const visibleTransfers = useMemo(
    () => allTransfers.slice(0, DEFAULT_VISIBLE_ROWS),
    [allTransfers]
  );

  const visibleInjuries = useMemo(
    () => allInjuries.slice(0, DEFAULT_VISIBLE_ROWS),
    [allInjuries]
  );

  // Modal rows (limited to MAX_MODAL_ROWS - Total + 19 competitions = 20 rows)
  const modalStats = useMemo(
    () => allStats.slice(0, MAX_MODAL_ROWS),
    [allStats]
  );

  // Ensure activeTab is valid for the visible tabs
  const effectiveActiveTab = visibleTabs.some(t => t.id === activeTab)
    ? activeTab
    : (visibleTabs[0]?.id ?? "transfers");

  // Get row count for current tab
  const totalRowCount = useMemo(() => {
    switch (effectiveActiveTab) {
      case "stats":
        return allStats.length;
      case "transfers":
        return allTransfers.length;
      case "injuries":
        return allInjuries.length;
      default:
        return 0;
    }
  }, [
    effectiveActiveTab,
    allStats.length,
    allTransfers.length,
    allInjuries.length,
  ]);

  // Data is loaded when player exists and queries completed.
  // leaguesLoading is included so per-competition rows never render with
  // unresolved league UUIDs before leagueNameMap populates (SE-4239).
  const isLoading =
    !player ||
    transfersLoading ||
    injuriesLoading ||
    basicStatsLoading ||
    leaguesLoading;

  // Handle tab change
  const handleTabChange = useCallback((tab: StatsTab) => {
    setActiveTab(tab);
  }, []);

  // Handle view all press
  const handleViewAllPress = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  // SE-5570: the SelectDropdown shell owns open/close, so the gluestack
  // uncontrolled state machine — and the SE-3823 setTimeout defer that worked
  // around its closeOnSelect desync — are gone; select directly.
  const handleSelectSeason = useCallback(
    (seasonId: string) => setSelectedSeason(seasonId),
    []
  );

  // 2. RETURN
  return (
    <PlayerStatsTableView
      isDark={isDark}
      activeTab={effectiveActiveTab}
      isLoading={isLoading}
      stats={visibleStats}
      transfers={visibleTransfers}
      injuries={visibleInjuries}
      allStats={modalStats}
      allTransfers={allTransfers}
      allInjuries={allInjuries}
      totalRowCount={totalRowCount}
      isModalOpen={isModalOpen}
      isSmallScreen={isSmallScreen}
      seasonOptions={seasonOptions}
      selectedSeason={effectiveSelectedSeason}
      visibleTabs={visibleTabs}
      onTabChange={handleTabChange}
      onViewAllPress={handleViewAllPress}
      onModalClose={handleModalClose}
      onSelectSeason={handleSelectSeason}
      selectedSeasonLabel={selectedSeasonLabel}
    />
  );
};

PlayerStatsTableContainer.displayName = "PlayerStatsTableContainer";

export default PlayerStatsTableContainer;
